// src/components/map/PlaceAutocomplete.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { LocationType, PotentialLocation } from '@/types/firestore';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { addPotentialLocation, setActiveInfoWindowId, selectPotentialLocations } from '@/store/slices/mapUiSlice';
import { selectAllLocations } from '@/store/slices/locationsSlice';
import { fetchActiveLocationData } from '@/store/slices/activeLocationSlice';
import { useMap } from '@vis.gl/react-google-maps';
import './autocomplete.css';
import { Timestamp } from 'firebase/firestore';

// Define the props type for PlaceAutocomplete
interface PlaceAutocompleteProps {
}

const PlaceAutocomplete: React.FC<PlaceAutocompleteProps> = () => {
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const map = useMap();
  
  const potentialLocations = useAppSelector(selectPotentialLocations);
  const allDbLocations = useAppSelector(selectAllLocations);
  
  const handleAddLocation = useCallback((place: google.maps.places.PlaceResult) => {
    if (!place || !place.geometry?.location) {
      console.warn('[PlaceAutocomplete] Invalid place data received.');
      setError('Selected place has no location data.');
      return;
    }

    setError(null);

    const googlePlaceId = place.place_id;
    const placeLatitude = place.geometry.location.lat();
    const placeLongitude = place.geometry.location.lng();

    let targetLocationId: string | null = null;
    let targetCoordinates: { lat: number; lng: number } | null = null;

    if (googlePlaceId) {
      // 1. Check existing potential locations
      const existingPotentialLocation = potentialLocations.find(
        (loc) => loc.googlePlaceId === googlePlaceId
      );
      if (existingPotentialLocation) {
        console.log('[PlaceAutocomplete] Found existing potential location:', existingPotentialLocation);
        targetLocationId = existingPotentialLocation.id || null;
        targetCoordinates = { 
          lat: existingPotentialLocation.coordinates.latitude, 
          lng: existingPotentialLocation.coordinates.longitude 
        };
        dispatch(setActiveInfoWindowId(targetLocationId));
      } else {
        // 2. Check existing Firestore locations (only if not found in potential)
        const existingDbLocation = allDbLocations.find(
          (loc) => loc.googlePlaceId === googlePlaceId
        );
        if (existingDbLocation) {
          console.log('[PlaceAutocomplete] Found existing DB location:', existingDbLocation);
          targetLocationId = existingDbLocation.id || null;
          targetCoordinates = { 
            lat: existingDbLocation.coordinates.latitude, 
            lng: existingDbLocation.coordinates.longitude 
          };
          if (targetLocationId) {
            dispatch(setActiveInfoWindowId(targetLocationId));
            dispatch(fetchActiveLocationData(targetLocationId));
          } else {
            console.warn('[PlaceAutocomplete] DB location found but has no ID:', existingDbLocation);
          }
        }
      }
    }

    // If no existing location was found and processed, create a new potential location
    if (!targetLocationId) {
      let locationType: LocationType = 'other';
      if (place.types) {
        if (place.types.includes('shop') || place.types.includes('store') || place.types.includes('convenience_store') || place.types.includes('liquor_store')) {
          locationType = 'shop';
        } else if (place.types.includes('bar') || place.types.includes('brewery')) {
          locationType = 'brewery';
        } else if (place.types.includes('restaurant') || place.types.includes('cafe')) {
          locationType = 'restaurant';
        }
      }

      const newPotentialLocation: PotentialLocation = {
        id: `potential-${googlePlaceId || Date.now()}`,
        googlePlaceId: googlePlaceId,
        name: place.name || "Unnamed Location",
        address: place.formatted_address || "Address unavailable",
        url: place.website,
        locationType: locationType,
        coordinates: { latitude: placeLatitude, longitude: placeLongitude },
        createdAt: Timestamp.now().toMillis(),
      };

      console.log('[PlaceAutocomplete] Adding new potential location:', newPotentialLocation);
      dispatch(addPotentialLocation({ location: newPotentialLocation, activate: true }));
      // For new potential locations, addPotentialLocation with activate:true should handle opening it.
      targetCoordinates = { lat: placeLatitude, lng: placeLongitude };
    }
    
    // Pan map and clear input if a location was processed or created
    if (targetCoordinates && map) {
      map.panTo(targetCoordinates);
    }
    if (inputRef.current) {
      inputRef.current.value = '';
    }

  }, [dispatch, map, potentialLocations, allDbLocations, setError]);

  useEffect(() => {
    if (!inputRef.current || !window.google || !map) return;
    
    if (!window.google.maps.places) {
      console.error("Google Maps Places library not loaded");
      setError("Places library failed to load.");
      return;
    }

    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      fields: ['name', 'formatted_address', 'geometry', 'website', 'types', 'place_id'],
    });
    
    autocomplete.bindTo('bounds', map);
    
    autocompleteRef.current = autocomplete;

    const placeChangedListener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      
      if (place) {
        handleAddLocation(place);
      } else {
        console.warn("[PlaceAutocomplete] Autocomplete returned no place details.");
      }
    });

    return () => {
      if (autocompleteRef.current) {
        autocompleteRef.current = null;
      }
      
      if (placeChangedListener) {
        google.maps.event.removeListener(placeChangedListener);
      }
    };
  }, [map, handleAddLocation, setError]);

  return (
    <div className="flex flex-col px-2 py-1">
      <input
        ref={inputRef}
        type="text"
        placeholder="Search Google for new locations"
        className="border border-input bg-background rounded-lg p-2 w-full ring-1 ring-ring focus:outline-none focus:ring-4 focus:ring-ring text-foreground"
      />
      {error && (
        <div className="text-xs text-destructive mt-1">{error}</div>
      )}
    </div>
  );
};

export default PlaceAutocomplete;